import { useMutation, UseMutationResult, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { ADMIN_API_URL_SUBPATH } from '@/constants';
import { axiosInstance, CreateEntityResponse, Group, Language, PRODUCT_GROUP } from '@/core';

type CreateGroupData = {
  name: Language;
  description?: Language;
  short_description?: Language;
  image_id?: number;
  ordering?: number;
  is_active: boolean;
};

export const useGroup = (config?: {
  onSuccess?: (data: AxiosResponse<CreateEntityResponse>) => void;
  onError?: (error: unknown) => void;
}) => {
  const queryConfig = {
    url: `${ADMIN_API_URL_SUBPATH}/${PRODUCT_GROUP}`,
  };

  const createMutation: UseMutationResult<
    AxiosResponse<CreateEntityResponse>,
    unknown,
    CreateGroupData,
    unknown
  > = useMutation({
    mutationKey: ['createGroup'],
    mutationFn: (data: CreateGroupData) => axiosInstance.post(queryConfig.url, data),
    onError: (error) => {
      config?.onError?.(error);
    },
    onSuccess: (data) => {
      config?.onSuccess?.(data);
    },
  });

  const useGroupQuery = (id: number) =>
    useQuery<AxiosResponse<Group>>({
      queryKey: ['group', id],
      queryFn: () => axiosInstance.get(`${queryConfig.url}/${id}`),
      enabled: !!id,
    });

  const updateMutation = useMutation({
    mutationKey: ['updateGroup'],
    mutationFn: (data: Group) => axiosInstance.put(`${queryConfig.url}/${data.id}`, data),
  });

  const deleteMutation = useMutation({
    mutationKey: ['deleteGroup'],
    mutationFn: (id: number) => axiosInstance.delete(`${queryConfig.url}/${id}`),
  });

  const duplicateMutation = useMutation({
    mutationKey: ['duplicateGroup'],
    mutationFn: (id: number) => axiosInstance.post(`${queryConfig.url}/${id}/duplicate`),
  });

  const deactivateMutation = useMutation({
    mutationKey: ['deactivateGroup'],
    mutationFn: (id: number) => axiosInstance.put(`${queryConfig.url}/${id}`, { is_active: false }),
  });

  const activateMutation = useMutation({
    mutationKey: ['activateGroup'],
    mutationFn: (id: number) => axiosInstance.put(`${queryConfig.url}/${id}`, { is_active: true }),
  });

  const reorderMutation = useMutation({
    mutationKey: ['reorderGroup'],
    mutationFn: ({ id, position }: { id: number; position: number }) =>
      axiosInstance.put(`${queryConfig.url}/${id}/reorder`, { position }),
  });

  return {
    mutations: {
      createMutation,
      updateMutation,
      deleteMutation,
      duplicateMutation,
      deactivateMutation,
      activateMutation,
      reorderMutation,
    },
    queries: {
      useGroupQuery,
    },
  };
};
